import React, { memo, useState, useCallback, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { useGetList, useNotify } from 'react-admin';
import { Button, Typography, Box, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import dataProvider from '../../providers/dataProvider';
import AddProduct from './AddProduct';
import EditPageItem from './EditPageItem';

const styles = {
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
};

const EditPage = () => {
  const notify = useNotify();
  const { data: listData, isLoading } = useGetList('admin/advertisement', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'order', order: 'ASC' },
  });

  const [advertisementProducts, changeAdvertisementProducts] = useState([]);

  useEffect(() => {
    if (listData && listData.length > 0) {
      changeAdvertisementProducts(listData);
    }
  }, [listData]);

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const dragCard = advertisementProducts[dragIndex];

      changeAdvertisementProducts(
        update(advertisementProducts, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard],
          ],
        }),
      );
    },
    [advertisementProducts],
  );

  const updateOrder = useCallback(() => {
    changeAdvertisementProducts((old) => old.map((a, index) => ({ ...a, order: index + 1 })));
  }, []);

  const [isSaving, changeIsSaving] = useState(false);
  const handleSaveAdvertisement = useCallback(() => {
    changeIsSaving(true);
    dataProvider
      .create('admin/advertisement', { data: { advertisement: advertisementProducts } })
      .then(() => {
        changeIsSaving(false);
        notify('Program order saved successfully', { type: 'success' });
      })
      .catch(() => {
        changeIsSaving(false);
        notify('Failed to save program order', { type: 'error' });
      });
  }, [advertisementProducts, notify]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Program Order
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Drag programs to reorder them on the homepage.
      </Typography>
      <AddProduct
        advertisementProducts={advertisementProducts}
        changeAdvertisementProducts={changeAdvertisementProducts}
      />
      <div onDragEnd={updateOrder}>
        <DndProvider backend={HTML5Backend}>
          {advertisementProducts.map((item, index) => (
            <EditPageItem
              key={item.productId}
              item={item}
              changeArray={changeAdvertisementProducts}
              moveCard={moveCard}
              id={item.productId}
              index={index}
            />
          ))}
        </DndProvider>
      </div>
      <div style={styles.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAdvertisement}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        >
          {isSaving ? 'Saving...' : 'Save Order'}
        </Button>
      </div>
    </Box>
  );
};

export default memo(EditPage);
